// ============================================================================
// LECTURES — LifeOS School.dc.html, option 6b.
//
// One row per lecture, four pipeline states, and the transcribed one expands into its AI summary.
//
// TWO THINGS THIS COMPONENT REFUSES TO DO:
//
//   1. IT WILL NOT NAME A LECTURE IT HAS NOT HEARD. The title cell is filled only for a
//      transcribed lecture, because the title is read off the recording. The mockup does the same
//      thing — its 'not recorded', 'processing' and 'audio uploaded' rows have an empty title —
//      and it is not a layout accident: a title on an unrecorded lecture is a guess about what a
//      class you did not record was about.
//
//   2. IT WILL NOT RENDER AN UNLABELLED SUMMARY. The expanded body goes through <Provenanced>,
//      so a summary that cannot say which model wrote it and from what does not appear at all.
//      See components/school/Provenance.tsx.
// ============================================================================

import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import {
  courseSlug,
  formatShortDate,
  lectureLabel,
  lectureRef,
  relativeDay,
  type Lecture,
} from '@app/shared'

import { SkeletonRows } from '../Card'
import { useLectureSummary } from '../../lib/data'
import { ChevronDown, Cards, DocLines, Mic, QuestionCircle } from './icons'
import { durationMinutes, hueVar, lecturePipelineCounts } from './derive'
import { LectureStatusChip, Unavailable } from './Chrome'
import { DownloadTranscriptButton } from './DownloadTranscript'
import { Eyebrow, Provenanced, ProvenanceLabel } from './Provenance'

export function LectureList({
  lectures,
  isPending,
  error,
  today,
}: {
  lectures: Lecture[] | undefined
  isPending: boolean
  error: unknown
  today: string
}) {
  const rows = lectures ?? []
  const counts = lecturePipelineCounts(rows)
  // The newest transcribed lecture opens by default: it is the one with something to read, and a
  // list of collapsed rows makes the study material look like it is not there.
  const firstTranscribed = rows.find((l) => l.status === 'transcribed')
  const [openId, setOpenId] = useState<string | null>(null)
  const effectiveOpen = openId ?? firstTranscribed?.id ?? null

  return (
    <section className="flex flex-none flex-col gap-[9px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <Mic size={16} strokeWidth={1.7} className="flex-none text-muted" />
        <h2 className="text-[13px] font-semibold leading-none">Lectures</h2>
        {lectures && (
          <span className="text-[11px] font-normal leading-none text-microcopy">
            {counts.recorded} recorded · {counts.transcribed} transcribed
          </span>
        )}
        {firstTranscribed && (
          <Link
            to={`/school/${courseSlug(firstTranscribed.courseCode)}/lecture/${lectureRef(firstTranscribed)}`}
            className="ml-auto text-[11px] font-medium leading-none text-success-dot hover:underline"
          >
            Study mode
          </Link>
        )}
      </header>

      {isPending && <SkeletonRows rows={4} />}
      <Unavailable error={error} />
      {lectures && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy">
          No lecture has been held in this course yet.
        </p>
      )}

      <div className="flex flex-col gap-[7px]">
        {rows.map((lecture) => (
          <LectureRow
            key={lecture.id}
            lecture={lecture}
            today={today}
            open={effectiveOpen === lecture.id}
            onToggle={() => setOpenId(effectiveOpen === lecture.id ? '' : lecture.id)}
          />
        ))}
      </div>
    </section>
  )
}

function LectureRow({
  lecture,
  today,
  open,
  onToggle,
}: {
  lecture: Lecture
  today: string
  open: boolean
  onToggle: () => void
}) {
  const expandable = lecture.status === 'transcribed'
  const duration = durationMinutes(lecture.durationSec)
  // 'today' / 'yesterday' only. Anything older would just repeat the date already in column one.
  const nearby = ['today', 'yesterday'].includes(relativeDay(lecture.date, today))
    ? relativeDay(lecture.date, today)
    : null

  return (
    <div
      className={[
        'rounded-row border',
        open && expandable ? 'border-line-school bg-surface-school' : 'border-fill bg-surface',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={expandable ? onToggle : undefined}
        aria-expanded={expandable ? open : undefined}
        disabled={!expandable}
        title={
          expandable
            ? undefined
            : 'There is no transcript for this lecture yet, so there is nothing to open.'
        }
        style={{ '--mo-rule': hueVar(lecture.courseCode) } as CSSProperties}
        className={[
          'mo-rule flex w-full items-center gap-[13px] px-[13px] py-[7px] text-left',
          expandable ? 'cursor-pointer' : 'cursor-default',
        ].join(' ')}
      >
        <span className="w-[52px] flex-none text-[12px] font-semibold leading-none text-ink-2">
          {formatShortDate(lecture.date)}
        </span>
        <span className="w-[52px] flex-none text-[11.5px] font-normal leading-none text-microcopy">
          {duration ?? nearby ?? ''}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium leading-[1.25]">
          {/* Only a transcribed lecture has a title we actually heard. See the file header. */}
          {lecture.status === 'transcribed' ? lectureLabel(lecture) : ''}
        </span>
        <LectureStatusChip status={lecture.status} />
        <ChevronDown
          size={14}
          className={[
            'flex-none text-microcopy transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]',
            open && expandable ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>

      {open && expandable && <LectureSummaryBody lecture={lecture} />}
    </div>
  )
}

function LectureSummaryBody({ lecture }: { lecture: Lecture }) {
  const { data: summary, isPending, error } = useLectureSummary(lecture.id)
  const to = `/school/${courseSlug(lecture.courseCode)}/lecture/${lectureRef(lecture)}`

  return (
    <div className="flex flex-col gap-2 pb-[10px] pl-[117px] pr-[13px]">
      {isPending && <SkeletonRows rows={3} />}
      {error && (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          {error instanceof Error ? error.message : 'The summary could not be loaded.'}
        </p>
      )}

      <Provenanced
        artifact={summary}
        fallback={
          !isPending && !error ? (
            // Absence, said out loud. An expanded row with nothing in it looks broken; an
            // expanded row that says why is just true.
            <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
              {/* This used to say "nothing generates one automatically", which stopped being true
                  when finishing a transcription started one (worker routes/jobs.ts, step 5b). It
                  says "usually" because the generation is best-effort and deliberately swallowed:
                  a lecture whose summary failed is still a transcribed lecture, and the button
                  below is how it gets asked for again. Promising a summary that never arrives
                  would be worse than this sentence. */}
              This lecture has a transcript but no summary yet. One is usually written on its own
              a moment after transcribing — if it has not appeared, open study mode below and ask
              for it.
            </p>
          ) : null
        }
      >
        {(artifact) => (
          <>
            <div className="flex flex-col gap-[7px]">
              <div className="flex items-center gap-[7px]">
                <Eyebrow>AI SUMMARY</Eyebrow>
                <ProvenanceLabel what="generated from transcript" artifact={artifact} />
              </div>
              {artifact.bullets.map((bullet) => (
                <div key={bullet} className="flex gap-2">
                  <span className="mt-[6px] size-1 flex-none rounded-full bg-school" aria-hidden />
                  <p className="text-[12px] leading-[1.5] text-ink-2 text-pretty">{bullet}</p>
                </div>
              ))}
            </div>

            {artifact.keyConcepts.length > 0 && (
              <div className="flex flex-col gap-[7px]">
                <Eyebrow>KEY CONCEPTS</Eyebrow>
                <div className="flex flex-wrap items-center gap-[7px]">
                  {artifact.keyConcepts.map((concept) => (
                    <span
                      key={concept}
                      className="rounded-pill bg-fill-2 px-[9px] py-1 text-[10.5px] font-medium leading-none text-ink-2"
                    >
                      {concept}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Provenanced>

      <div className="flex items-center gap-[9px] pt-[2px]">
        <Link
          to={to}
          style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
          className="mo-ink mo-press flex items-center gap-[7px] rounded-button bg-ink px-3 py-[7px] text-[11.5px] font-semibold leading-none text-surface"
        >
          <DocLines size={13} strokeWidth={1.9} className="text-school-lift" />
          Full transcript
        </Link>
        <Link
          to={`${to}#flashcards`}
          style={{ '--mo-rule': 'var(--color-interests)' } as CSSProperties}
          className="mo-ink mo-press flex items-center gap-[7px] rounded-button border border-line-2 bg-surface px-3 py-[7px] text-[11.5px] font-semibold leading-none text-ink-2"
        >
          <Cards size={13} className="text-interests" />
          Flashcards
        </Link>
        <Link
          to={`${to}#practice`}
          style={{ '--mo-rule': 'var(--color-learning)' } as CSSProperties}
          className="mo-ink mo-press flex items-center gap-[7px] rounded-button border border-line-2 bg-surface px-3 py-[7px] text-[11.5px] font-semibold leading-none text-ink-2"
        >
          <QuestionCircle size={13} className="text-learning" />
          Practice
        </Link>
        {/* The transcript as a file, beside the three ways of reading it here (owner,
            2026-09-17). Only a transcribed lecture reaches this body, so there is always
            something to download. */}
        <DownloadTranscriptButton lectureId={lecture.id} variant="pill" />
      </div>
    </div>
  )
}
